import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { contentAPI, settingsAPI, getMediaUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Mic, Clock, FileText, Eye, Play, X, Volume2, FileAudio, ChevronDown, ChevronUp, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ExpressionOrale() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [blocs, setBlocs] = useState([]);
  const [selectedBloc, setSelectedBloc] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showTranscription, setShowTranscription] = useState({});
  const [accessLimit, setAccessLimit] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load all data based on user's center
        let packs, siteSettings;
        
        if (user?.center_id) {
          // User belongs to a center
          [packs, siteSettings] = await Promise.all([
            settingsAPI.getCenterPacks(user.center_id),
            settingsAPI.getCenterAccessConfig(user.center_id)
          ]);
        } else {
          // No center - use global settings
          [packs, siteSettings] = await Promise.all([
            settingsAPI.getPacks(),
            settingsAPI.getSiteSettings()
          ]);
        }
        
        const loadedBlocs = await contentAPI.getBlocs('expression_orale');
        setBlocs(Array.isArray(loadedBlocs) ? loadedBlocs : []);
        
        if (user?.role === 'admin' || user?.role === 'super_admin') {
          setAccessLimit(9999);
        } else {
          const freeBlocs = siteSettings?.free_blocs?.expression_orale || 1;
          if (user?.type_pack && user?.fin_abonnement) {
            const isSubscriptionActive = new Date(user.fin_abonnement) > new Date();
            if (isSubscriptionActive) {
              const userPack = packs?.find(p => p.id === user.type_pack || p.name === user.type_pack);
              if (userPack?.blocs_limits?.expression_orale) {
                const packLimit = userPack.blocs_limits.expression_orale;
                setAccessLimit(packLimit === -1 ? 9999 : packLimit);
              } else {
                setAccessLimit(freeBlocs);
              }
            } else {
              setAccessLimit(freeBlocs);
            }
          } else {
            setAccessLimit(freeBlocs);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Erreur de chargement');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [user]);

  const isBlocLocked = (index) => {
    return index >= accessLimit;
  };

  const handleStartExam = (blocId, index) => {
    if (isBlocLocked(index)) {
      toast.info('🔒 Contenu Premium ! Découvrez nos forfaits pour débloquer tous les blocs.', {
        duration: 4000,
        action: {
          label: 'Voir les forfaits',
          onClick: () => navigate('/tarification')
        }
      });
      navigate('/tarification');
      return;
    }
    navigate(`/modules/expression-orale/exercise?bloc=${blocId}`);
  };

  const handlePreview = (bloc, index) => {
    if (isBlocLocked(index)) {
      toast.info('🔒 Contenu Premium ! Découvrez nos forfaits pour débloquer tous les blocs.', {
        duration: 4000,
        action: {
          label: 'Voir les forfaits',
          onClick: () => navigate('/tarification')
        }
      });
      navigate('/tarification');
      return;
    }
    setSelectedBloc(bloc);
    setShowPreview(true);
    setShowTranscription({});
  };

  const toggleTranscription = (tacheIndex) => {
    setShowTranscription(prev => ({
      ...prev,
      [tacheIndex]: !prev[tacheIndex]
    }));
  };

  const tacheLabels = {
    1: 'Entretien dirigé',
    2: 'Interaction',
    3: 'Point de vue'
  };

  return (
    <Layout>
      <div className="space-y-8 animate-in">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center p-4 bg-orange-500/10 rounded-2xl mb-4">
            <Mic className="h-12 w-12 text-orange-500" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Expression Orale</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Entraînez-vous à parler en français. Chaque bloc contient 3 tâches : 
            entretien dirigé, exercice d'interaction et expression d'un point de vue.
          </p>
        </div>

        {/* Stats Row */}
        <div className="flex justify-center gap-8 text-center">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-orange-500" />
            <span className="text-sm text-muted-foreground">12 min</span>
          </div>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-orange-500" />
            <span className="text-sm text-muted-foreground">3 tâches</span>
          </div>
          <div className="flex items-center gap-2">
            <Mic className="h-5 w-5 text-orange-500" />
            <span className="text-sm text-muted-foreground">Oral</span>
          </div>
        </div>

        {/* Blocs Grid */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-12 w-12 text-orange-500 animate-spin mb-4" />
            <p className="text-muted-foreground">Chargement des exercices...</p>
          </div>
        ) : blocs.length === 0 ? (
          <div className="bg-card p-12 rounded-xl border text-center max-w-2xl mx-auto">
            <Mic className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-semibold mb-2">Aucun exercice disponible</h3>
            <p className="text-muted-foreground">
              L'administrateur n'a pas encore créé de blocs d'exercices pour ce module.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {blocs.map((bloc, index) => {
              const locked = isBlocLocked(index);
              const tasks = bloc.tasks || bloc.taches || [];
              
              return (
                <div
                  key={bloc.id}
                  data-testid={`bloc-eo-${bloc.number}`}
                  onClick={() => locked && navigate('/tarification')}
                  className={`bg-card rounded-2xl border-2 transition-all overflow-hidden group relative ${
                    locked 
                      ? 'border-gray-300 dark:border-gray-700 opacity-75 cursor-pointer hover:border-orange-500/50' 
                      : 'border-border hover:border-orange-500/50 hover:shadow-lg'
                  }`}
                >
                  {/* Lock Overlay */}
                  {locked && (
                    <div className="absolute inset-0 bg-gray-900/10 dark:bg-gray-900/30 z-10 flex items-center justify-center">
                      <div className="bg-white dark:bg-gray-800 rounded-full p-4 shadow-lg">
                        <Lock className="h-8 w-8 text-gray-500 dark:text-gray-400" />
                      </div>
                    </div>
                  )}

                  {/* Card Header with Number */}
                  <div className="p-6 pb-4">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className={`text-2xl font-bold ${locked ? 'text-gray-500' : 'text-orange-500'}`}>
                        {bloc.name}
                      </h3>
                      {locked ? (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                          Premium
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                          12 min
                        </span>
                      )}
                    </div>
                    
                    {/* Tâches details */}
                    <div className="space-y-2 mb-4">
                      {tasks.length > 0 ? tasks.map((tache) => (
                        <div
                          key={tache.tache}
                          className="flex items-center justify-between text-sm bg-secondary/50 rounded-lg px-3 py-2"
                        >
                          <div className="flex items-center space-x-2">
                            <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              locked ? 'bg-gray-300 text-gray-600' : 'bg-orange-500 text-white'
                            }`}>
                              {tache.tache}
                            </div>
                            <span className="text-muted-foreground truncate max-w-[140px]">
                              {tache.titre || tacheLabels[tache.tache]}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground font-medium">
                            {tache.duree || (tache.tache === 1 ? 2 : 5)} min
                          </span>
                        </div>
                      )) : (
                        <p className="text-sm text-muted-foreground">3 tâches • Entretien, Interaction, Point de vue</p>
                      )}
                    </div>

                    {/* Info row */}
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-4 pt-2 border-t border-border">
                      <span>3 tâches</span>
                      <span>Enregistrement vocal</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="p-6 pt-0">
                    {locked ? (
                      <button
                        onClick={() => navigate('/tarification')}
                        data-testid={`unlock-bloc-eo-${bloc.number}`}
                        className="w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600"
                      >
                        <Lock className="h-5 w-5" />
                        <span>Débloquer</span>
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePreview(bloc, index)}
                          data-testid={`preview-bloc-eo-${bloc.number}`}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-orange-500/30 text-orange-600 hover:bg-orange-500 hover:text-white transition-all font-semibold"
                        >
                          <Eye className="h-5 w-5" />
                          <span>Aperçu</span>
                        </button>
                        <button
                          onClick={() => handleStartExam(bloc.id, index)}
                          data-testid={`start-bloc-eo-${bloc.number}`}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-orange-500 text-white hover:bg-orange-600 transition-all font-semibold"
                        >
                          <Play className="h-5 w-5" />
                          <span>Commencer</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && selectedBloc && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-background rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
              {/* Modal Header */}
              <div className="p-6 border-b flex items-center justify-between bg-orange-500/10">
                <div>
                  <h3 className="text-xl font-bold">{selectedBloc.name}</h3>
                  <p className="text-sm text-muted-foreground">Aperçu des sujets avant l'examen</p>
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 rounded-lg hover:bg-secondary transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
                {(selectedBloc.tasks || selectedBloc.taches || []).map((tache, index) => (
                  <div
                    key={tache.tache}
                    className="bg-card p-5 rounded-xl border"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
                          {tache.tache}
                        </div>
                        <div>
                          <h4 className="font-semibold">Tâche {tache.tache} : {tache.titre || tacheLabels[tache.tache]}</h4>
                          <p className="text-xs text-muted-foreground">{tache.duree} minutes</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {/* Consigne */}
                      <div className="bg-secondary/30 p-4 rounded-lg">
                        <p className="text-sm font-medium text-orange-600 dark:text-orange-400 mb-2">Consigne :</p>
                        <p className="text-sm">{tache.instruction}</p>
                      </div>

                      {/* Sujet */}
                      <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
                        <p className="text-sm font-medium text-orange-600 dark:text-orange-400 mb-2">Sujet :</p>
                        <p className="text-sm whitespace-pre-wrap">{tache.sujet}</p>
                      </div>

                      {/* Modèle audio - Masqué par défaut */}
                      {(tache.modeleAudioUrl || tache.modeleTexte) && (
                        <div className="border-t pt-3 mt-3">
                          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                            <div className="flex items-center space-x-2 mb-3">
                              <FileAudio className="h-5 w-5 text-green-600" />
                              <span className="text-sm font-medium text-green-700 dark:text-green-400">
                                Modèle de correction (Audio)
                              </span>
                            </div>
                            
                            {/* Audio Player */}
                            {tache.modeleAudioUrl && (
                              <audio controls controlsList="nodownload" className="w-full h-10 mb-3">
                                <source src={getMediaUrl(tache.modeleAudioUrl)} type="audio/mpeg" />
                                Votre navigateur ne supporte pas l'audio.
                              </audio>
                            )}

                            {/* Transcription Toggle */}
                            {tache.modeleTexte && (
                              <>
                                <button
                                  onClick={() => toggleTranscription(index)}
                                  data-testid={`toggle-transcription-${index}`}
                                  className="flex items-center justify-between w-full p-2 rounded-lg bg-green-100 dark:bg-green-900/40 hover:bg-green-200 dark:hover:bg-green-900/60 transition-colors"
                                >
                                  <span className="text-xs font-medium text-green-700 dark:text-green-400">
                                    {showTranscription[index] ? 'Masquer' : 'Afficher'} la transcription
                                  </span>
                                  {showTranscription[index] ? (
                                    <ChevronUp className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4 text-green-600" />
                                  )}
                                </button>
                                
                                {showTranscription[index] && (
                                  <div className="mt-2 p-3 bg-white dark:bg-gray-800 rounded-lg border">
                                    <p className="text-sm whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                                      {tache.modeleTexte}
                                    </p>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t bg-secondary/20">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <p className="text-sm text-muted-foreground">
                    Prêt ? Vous aurez 12 minutes pour les 3 tâches.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <button
                      onClick={() => setShowPreview(false)}
                      className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
                    >
                      Fermer
                    </button>
                    <button
                      onClick={() => {
                        setShowPreview(false);
                        handleStartExam(selectedBloc.id);
                      }}
                      className="flex items-center justify-center space-x-2 px-6 py-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors font-semibold"
                    >
                      <Play className="h-4 w-4" />
                      <span>Commencer</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
